import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const languages = [
  { code: "en", label: "English", short: "EN" },
  { code: "pl", label: "Polski", short: "PL" },
  { code: "es", label: "Español", short: "ES" },
  { code: "fr", label: "Français", short: "FR" },
];

export default function SiteHeader() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => i18n.language?.startsWith(l.code)) || languages[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const switchLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  const handleAnchorClick = (hash: string) => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate(`/#${hash}`);
    }
  };

  const navLinks = [
    { label: t("nav.home"), to: "/", icon: "ri-home-4-line", type: "link" as const },
    { label: t("nav.categories"), hash: "categories", icon: "ri-apps-line", type: "anchor" as const },
    { label: t("nav.shoppingGuide"), to: "https://kaspreadsheet.com", icon: "ri-shopping-bag-3-line", type: "external" as const },
    { label: t("nav.guides"), hash: "guides", icon: "ri-book-open-line", type: "anchor" as const },
    { label: t("nav.faq"), hash: "faq", icon: "ri-questionnaire-line", type: "anchor" as const },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/98 backdrop-blur border-b border-background-200/80 shadow-sm"
          : "bg-background-50/98 backdrop-blur border-b border-background-200/50"
      }`}
    >
      <div className="mx-auto px-4 md:px-6">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap" onClick={() => setMobileOpen(false)}>
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-stack-line text-lg"></i>
            </span>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 tracking-tight">
              {t("brand.name")}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.type === "anchor") {
                return (
                  <button
                    key={link.hash}
                    type="button"
                    onClick={() => handleAnchorClick(link.hash!)}
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <i className={`${link.icon} text-base`}></i>
                    {link.label}
                  </button>
                );
              }
              if (link.type === "external") {
                return (
                  <a
                    key={link.to}
                    href={link.to}
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <i className={`${link.icon} text-base`}></i>
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-700 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <i className={`${link.icon} text-base`}></i>
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 hover:border-background-300 transition-colors cursor-pointer"
                aria-label="Language"
              >
                <i className="ri-global-line text-sm"></i>
                <span>{current.short}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => switchLang(l.code)}
                      className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2 text-sm text-left transition-colors cursor-pointer hover:bg-background-100 ${
                        current.code === l.code ? "text-primary-600 font-semibold" : "text-foreground-700"
                      }`}
                    >
                      <span>{l.label}</span>
                      {current.code === l.code && <i className="ri-check-line text-sm"></i>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t("buttons.buyNow")}
              <i className="ri-external-link-line text-sm"></i>
            </a>

            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 bg-background-50 text-foreground-800 lg:hidden cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-5 max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => {
              if (link.type === "anchor") {
                return (
                  <button
                    key={link.hash}
                    type="button"
                    onClick={() => handleAnchorClick(link.hash!)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-primary-50 cursor-pointer text-left"
                  >
                    <i className={`${link.icon} text-base text-primary-600`}></i>
                    {link.label}
                  </button>
                );
              }
              if (link.type === "external") {
                return (
                  <a
                    key={link.to}
                    href={link.to}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-primary-50 cursor-pointer"
                  >
                    <i className={`${link.icon} text-base text-primary-600`}></i>
                    {link.label}
                  </a>
                );
              }
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-3 text-sm font-semibold text-foreground-700 hover:bg-primary-50 cursor-pointer"
                >
                  <i className={`${link.icon} text-base text-primary-600`}></i>
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile category quick links */}
          <div className="mt-4 pt-4 border-t border-background-200">
            <p className="px-3 text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
              {t("nav.categories")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="h-8 w-8 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={cat.image}
                      alt=""
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <span className="truncate">{t(`categories.${cat.id}`)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-background-200">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 cursor-pointer"
            >
              {t("buttons.buyNow")}
              <i className="ri-external-link-line text-sm"></i>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}